import { arrayOfComments } from "~/schemas/schema.comments.jsx";
import * as schemaDiscussions from "~/schemas/schema.discussions.jsx";
import * as schemaTasks from "~/schemas/schema.tasks.jsx";
import * as actionTypes from "~/store/action_types";
import Angie from "~/utils/angie.jsx";
import { fetch } from "~/utils/fetch.jsx";
import { encodeData } from "~/utils/utils";
import * as trashActions from "./actions.trash.jsx";

export const fetchDiscussion = (projectId, discussionId) => {
  const url = `${Angie.api_url}/projects/${projectId}/discussions/${discussionId}`;
  return {
    types: [
      actionTypes.FETCH_DISCUSSION_LOADING,
      actionTypes.FETCH_DISCUSSION_SUCCESS,
      actionTypes.FETCH_DISCUSSION_ERROR,
    ],
    callAPI: () =>
      fetch({
        url,
      }),
    payload: {
      projectId,
      discussionId,
      schema: {
        single: schemaDiscussions.discussion,
        comments: arrayOfComments,
      },
    },
    cache: {
      key: url,
    },
  };
};

export const prefetchDiscussion = (projectId, discussionId) => (dispatch) => {
  return dispatch({
    types: [
      actionTypes.PREFETCH_DISCUSSION_LOADING,
      actionTypes.PREFETCH_DISCUSSION_SUCCESS,
      actionTypes.PREFETCH_DISCUSSION_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/discussions/${discussionId}`,
        prefetchNext: true,
      }),
    payload: {
      projectId,
      discussionId,
      schema: {
        single: schemaDiscussions.discussion,
        comments: arrayOfComments,
      },
    },
  });
};

export const fetchDiscussions = (projectId, page = 1, sortBy = "updated", order = "desc", signal) => {
  let query = encodeData({ page, sort_by: sortBy, order });

  if (query) {
    query = "?" + query;
  }

  const url = `${Angie.api_url}/projects/${projectId}/discussions/for-screen${query}`;
  return {
    types: [
      actionTypes.FETCH_DISCUSSIONS_LOADING,
      actionTypes.FETCH_DISCUSSIONS_SUCCESS,
      actionTypes.FETCH_DISCUSSIONS_ERROR,
    ],
    callAPI: () => {
      return fetch({
        url,
        withHeaders: true,
        signal,
      });
    },
    payload: {
      projectId,
      page,
      sortBy,
      order,
      schema: {
        discussions: schemaDiscussions.arrayOfDiscussions,
        comments: arrayOfComments,
      },
    },
  };
};

export const editDiscussion = (projectId, discussionId, attributes) => {
  return {
    types: [
      actionTypes.EDIT_DISCUSSION_LOADING,
      actionTypes.EDIT_DISCUSSION_SUCCESS,
      actionTypes.EDIT_DISCUSSION_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/discussions/${discussionId}`,
        method: "PUT",
        body: attributes,
      }),
    payload: {
      projectId,
      discussionId,
      attributes,
      schema: {
        single: schemaDiscussions.discussion,
      },
    },
  };
};

export const addDiscussion = (projectId, attributes) => {
  return {
    types: [actionTypes.ADD_DISCUSSION_LOADING, actionTypes.ADD_DISCUSSION_SUCCESS, actionTypes.ADD_DISCUSSION_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/discussions`,
        method: "POST",
        body: attributes,
      }),
    payload: {
      projectId,
      attributes,
      schema: {
        single: schemaDiscussions.discussion,
      },
    },
  };
};

export const emptyDiscussions = () => {
  return {
    type: actionTypes.EMPTY_DISCUSSIONS,
  };
};

export const moveToTrash = (id) => {
  const objectType = `Discussion`;
  return {
    ...trashActions.moveToTrash(objectType, id),
    types: [
      actionTypes.MOVE_DISCUSSION_TO_TRASH_LOADING,
      actionTypes.MOVE_DISCUSSION_TO_TRASH_SUCCESS,
      actionTypes.MOVE_DISCUSSION_TO_TRASH_ERROR,
    ],
    payload: {
      objectType,
      id,
      schema: {
        single: schemaDiscussions.discussion,
      },
    },
  };
};

export const restoreFromTrash = (id) => {
  const objectType = `Discussion`;
  return {
    ...trashActions.restoredFromTrash(objectType, id),
    types: [
      actionTypes.RESTORE_DISCUSSION_FROM_TRASH_LOADING,
      actionTypes.RESTORE_DISCUSSION_FROM_TRASH_SUCCESS,
      actionTypes.RESTORE_DISCUSSION_FROM_TRASH_ERROR,
    ],
    payload: {
      objectType,
      id,
      schema: {
        single: schemaDiscussions.discussion,
      },
    },
  };
};

export const addDiscussionFromEventStream = (data) => {
  return {
    type: actionTypes.ADD_DISCUSSION_FROM_EVENT_STREAM,
    response: data,
    schema: schemaDiscussions.discussion,
  };
};

export const updateDiscussionFromEventStream = (data) => {
  return {
    type: actionTypes.UPDATE_DISCUSSION_FROM_EVENT_STREAM,
    response: data,
    schema: schemaDiscussions.discussion,
  };
};

export const moveDiscussionToTrashFromEventStream = (data) => {
  return {
    type: actionTypes.MOVE_DISCUSSION_TO_TRASH_FROM_EVENT_STREAM,
    response: data,
    schema: schemaDiscussions.discussion,
  };
};

export const restoreDiscussionFromTrashFromEventStream = (data) => {
  return {
    type: actionTypes.RESTORE_DISCUSSION_FROM_TRASH_FROM_EVENT_STREAM,
    response: data,
    schema: schemaDiscussions.discussion,
  };
};

export const discussionMovedToProjectFromEventStream = (data) => {
  return {
    type: actionTypes.DISCUSSION_MOVED_TO_PROJECT_FROM_EVENT_STREAM,
    response: data,
    schema: schemaDiscussions.discussion,
  };
};

export const convertToTask = (projectId, discussionId) => {
  return {
    types: [
      actionTypes.CONVERT_DISCUSSION_TO_TASK_LOADING,
      actionTypes.CONVERT_DISCUSSION_TO_TASK_SUCCESS,
      actionTypes.CONVERT_DISCUSSION_TO_TASK_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/discussions/${discussionId}/promote-to-task`,
        method: "POST",
      }),
    payload: {
      projectId,
      discussionId,
      schema: {
        single: schemaTasks.task,
      },
    },
  };
};

export const fetchReadStatues = (projectId) => {
  return {
    types: [
      actionTypes.FETCH_DISCUSSION_READ_STATUSES_LOADING,
      actionTypes.FETCH_DISCUSSION_READ_STATUSES_SUCCESS,
      actionTypes.FETCH_DISCUSSION_READ_STATUSES_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/discussions/read-status`,
      }),
    payload: {
      projectId,
    },
  };
};

export const moveCopyDiscussionToProject = (projectId, id, attributes) => {
  return {
    types: [
      actionTypes.MOVE_DISCUSSION_TO_PROJECT_LOADING,
      actionTypes.MOVE_DISCUSSION_TO_PROJECT_SUCCESS,
      actionTypes.MOVE_DISCUSSION_TO_PROJECT_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/discussions/${id}/move-to-project`,
        method: `put`,
        body: attributes,
      }),
    payload: {
      projectId,
      discussionId: id,
      copy: attributes.copy,
      newProjectId: attributes.project_id,
      schema: {
        single: schemaDiscussions.discussion,
      },
    },
  };
};
